(ns dashboard.entity.document.pdf
  "The principle contract of the dashboard.entity component namespaces are this:
     1. All components will NOT depend on keyword references to subs and events in
        external NSes, but rather receive data as arguments, functions and callbacks.
     2. All internal reliance on the re-frame app DB must be internally contained.
     3. All re-frame app subs and events must NOT be directly referenced by outside
        NSes, but rather via GETTER and SETTER functions - the same is true of internal
        calls - prefer using parameterized functions.
     4. The entity is a UI component that may encapsulate low-level business rules on
        UI controls, but is not responsible for hydrating components with data or entrenched
        references to component namespaces. The UI is customizable, and top-level entities
        should have configurable and interchangable UI elements."
  (:require
   [shadow.cljs.modern :refer (js-await)]
   [shadow.esm :as esm]))

(defn render-paragraphs
  "Used for rendering previously-hihghlighted evidence, that is stored and
   recalled from the DB.
   Each highlight (ATM) is a vector of maps where each map details the
   highlighted content on a specific page of the document."
  [pdf-doc paragraphs]
  (when (and pdf-doc (seq paragraphs))
    (->> paragraphs
         (group-by :page)
         (mapv
          (fn [[page coll]]
            (let [page (dec page)]
              (mapv
               (fn [{:keys [id analysis] :as paragraph}]
                 (when-let [highlight-node (.getElementById
                                            js/document
                                            (str "paragraphs-for-page-" page))]
                   (when-not (or (.getElementById js/document (str "paragraph-" id "-" page))
                                 (not (seq analysis)))
                     (let [wrapper (.createElement js/document "div")
                           bounding-box (-> paragraph
                                            :geometry
                                            :BoundingBox)]
                       (set! (.-id wrapper) (str "paragraph-" id "-" page))
                       (set! (.-className wrapper) "textLayer paragraph document-viewer--paragraph")
                       (set! (.-style wrapper)
                             (str "position: absolute; "
                                  "z-index: 30; "
                                  "left: " (* 100 (:Left bounding-box)) "%; "
                                  "top: calc(" (* 100 (:Top bounding-box)) "% - 4px); "
                                  "width: " (* 100 (:Width bounding-box)) "%; "
                                  "height: calc(" (* 100 (:Height bounding-box)) "% + 4px); "))

                       (.appendChild highlight-node wrapper)
                       (.setTimeout js/window
                                    (fn [_] (set! (.-data wrapper) id))
                                    0)))))
               coll)))))))

(defn render-diff-paragraphs
  "Used for rendering previously-hihghlighted evidence, that is stored and
   recalled from the DB.
   Each highlight (ATM) is a vector of maps where each map details the
   highlighted content on a specific page of the document."
  [pdf-doc paragraphs id-prefix]
  (when (and pdf-doc (seq paragraphs))
    (->> paragraphs
         (group-by :page)
         (mapv
          (fn [[page coll]]
            (let [page (dec page)]
              (mapv
               (fn [{:keys [id similarity] :as paragraph}]
                 (when-let [highlight-node (.getElementById
                                            js/document
                                            (str id-prefix
                                                 "paragraphs-for-page-"
                                                 page))]
                   (when (not (.getElementById js/document
                                               (str "paragraph-" id "-" page)))
                     (let [wrapper (.createElement js/document "div")
                           bounding-box (-> paragraph
                                            :geometry
                                            :BoundingBox)]
                       (set! (.-id wrapper) (str "paragraph-" id "-" page))
                       ; For debugging
                       ;(set! (.-innerHTML wrapper) (str similarity))
                       (set! (.-className wrapper) (str "textLayer paragraph document-viewer--paragraph"
                                                        (when (< similarity 0.25) " document-diff--no-matching-paragraph")
                                                        (when (= similarity 1) " document-diff--matching-paragraph")))
                       (set! (.-style wrapper)
                             (str "position: absolute; "
                                  "z-index: 30; "
                                  "left: " (* 100 (:Left bounding-box)) "%;"
                                  "top: calc(" (* 100 (:Top bounding-box)) "% - 4px);"
                                  "width: " (* 100 (:Width bounding-box)) "%;"
                                  "height: calc(" (* 100 (:Height bounding-box)) "% + 4px);"))

                       (.appendChild highlight-node wrapper)
                       (.setTimeout js/window
                                    (fn [_] (set! (.-data wrapper) id))
                                    0)))))
               coll)))))))

(defn render-search-result-paragraph
  "Used for rendering previously-hihghlighted evidence, that is stored and
   recalled from the DB.
   Each highlight (ATM) is a vector of maps where each map details the
   highlighted content on a specific page of the document."
  [pdf-doc {:keys [page document-paragraph-id] :as paragraph}]
  (when pdf-doc
    (let [page (dec page)]
      (when-let [highlight-node (.getElementById
                                 js/document
                                 (str "highlights-for-page-" page))]
        (doseq [elem (->> (.getElementsByClassName js/document "viewer--search-result-content")
                          array-seq)]
          (.remove (.-classList elem) "viewer--search-result-content"))

        (let [wrapper (.createElement js/document "div")
              bounding-box (-> paragraph :bounding-box)]
          (set! (.-id wrapper) (str "highlight-" document-paragraph-id "-" page))
          (set! (.-className wrapper) "viewer--search-result-content textLayer paragraph")
          (set! (.-style wrapper) (str "position: absolute; "
                                       "left: " (* 100 (:Left bounding-box)) "%; "
                                       "top: calc(" (* 100 (:Top bounding-box)) "% - 12px); "
                                       "width: " (* 100 (:Width bounding-box)) "%; "
                                       "height: calc(" (* 100 (:Height bounding-box)) "% + 12px); "))

          (.appendChild highlight-node wrapper)

          (.setTimeout js/window
                       (fn [_]
                         (set! (.-data wrapper) document-paragraph-id)
                         (.scrollIntoView wrapper
                                          #js{:behavior "smooth"
                                              :block "center"}))
                       0))))))

(defn render-text-layer
  [wrapper ^js page viewport page-number selected-paragraph ^js pdf on-load id-prefix]
  (when page
    (let [text-layer-div (.createElement js/document "div")
          highlght-layer-div (.createElement js/document "div")
          paragraphs-layer-div (.createElement js/document "div")]

      (set! (.-className highlght-layer-div) "highlightLayer textLayer")
      (set! (.-id highlght-layer-div) (str id-prefix "highlights-for-page-" (dec page-number)))
      (.appendChild wrapper highlght-layer-div)

      (set! (.-className paragraphs-layer-div) "paragraphsLayer textLayer")
      (set! (.-id paragraphs-layer-div) (str id-prefix "paragraphs-for-page-" (dec page-number)))
      (.appendChild wrapper paragraphs-layer-div)

      (set! (.-className text-layer-div) "textLayer")
      (set! (.-name text-layer-div) (str "page-" (dec page-number)))
      (.appendChild wrapper text-layer-div)

      ;; Render the text layer
      (-> (.getTextContent page)
          (.then
           (fn [^js text-content]
               (-> #js {:viewport viewport
                        :textContentSource text-content
                        :container text-layer-div}
                   (js/pdfjsLib.TextLayer.)
                   (.render (clj->js {:textContent text-content})))
               (when (= page-number (:page selected-paragraph))
                 (render-search-result-paragraph pdf selected-paragraph))
               (on-load (.-numPages pdf))))))))

(defn render-page
  [^js pdf pdf-ref selected-paragraph on-load width scale id-prefix page-number]
  (let [wrapper (.createElement js/document "div")
        canvas (.createElement js/document "canvas")]
    ;; The wrapper, positioned relatively, sets the 0,0 coordinates for the
    ;; absolute positioned child contents!
    (set! (.-style.position wrapper) "relative")
    (.appendChild wrapper canvas)

    (-> (.getPage pdf page-number)
        (.then (fn [^js page]
                 (when page
                   (let [scale scale
                         viewport (.getViewport page #js {:scale scale})]
                     (set! (.-height canvas) (.-height viewport))
                     (set! (.-width canvas) width #_(.-width viewport))

                     (set! (.-height wrapper) (.-height viewport))
                     (set! (.-width wrapper) (.-width viewport))

                     ;; Set the wrapper height/width based on the viewport and then append!
                     (.appendChild pdf-ref wrapper)

                     (let [context (.getContext canvas "2d")
                           renderContext #js {:canvasContext context
                                              :viewport viewport}]
                       (-> (.render page renderContext)
                           (.-promise)
                           (.then (fn []
                                    (render-text-layer wrapper
                                                       page
                                                       viewport
                                                       page-number
                                                       selected-paragraph
                                                       pdf
                                                       on-load
                                                       id-prefix)))
                           (.catch (fn [error]
                                     (js/console.error "Error rendering page:" error))))))))))))

(defn clear-paragraphs
  "Used for rendering previously-highlighted evidence, that is stored and
   recalled from the DB.
   Each highlight (ATM) is a vector of maps where each map details the
   highlighted content on a specific page of the document."
  []
  (->> (.querySelectorAll js/document ".paragraphsLayer")
       (mapv
        (fn [elem]
          (set! (.-innerHTML elem) "")))))

(defn clear-evidence
  "Used for rendering previously-highlighted evidence, that is stored and
   recalled from the DB.
   Each highlight (ATM) is a vector of maps where each map details the
   highlighted content on a specific page of the document."
  [pdf-doc highlighted id-prefix]
  (when (and pdf-doc (seq highlighted))
    (->> highlighted
         (mapv
          (fn [{{:keys [html-ranges
                        page-range]} :content-data}]
            (mapv (fn [page-index _]
                    (when-let [highlight-node (.getElementById
                                               js/document
                                               (str id-prefix "highlights-for-page-" page-index))]
                      (set! (.-innerHTML highlight-node) "")))
                  (range (:start page-range) (inc (:end page-range)))
                  html-ranges))))))


(defn render-evidence
  "Used for rendering previously-highlighted evidence, that is stored and
   recalled from the DB.
   Each highlight (ATM) is a vector of maps where each map details the
   highlighted content on a specific page of the document."
  [pdf-doc highlighted id-prefix]
  (when (and pdf-doc (seq highlighted))
    (->> highlighted
         (mapv
          (fn [{{:keys [html-ranges
                        page-range]} :content-data
                evidence-id :evidence-id}]
            (mapv (fn [page-index html-range]
                    (when-let [highlight-node (.getElementById
                                               js/document
                                               (str id-prefix
                                                    "highlights-for-page-"
                                                    page-index))]
                      (when-not (.getElementById js/document (str id-prefix "highlight-" evidence-id "-" page-index))
                        (let [wrapper (.createElement js/document "div")]
                          (set! (.-id wrapper) (str id-prefix "highlight-" evidence-id "-" page-index))
                          (set! (.-className wrapper) "viewer--highlighted-content")
                          (set! (.-innerHTML wrapper) (apply str html-range))
                          (.appendChild highlight-node wrapper)

                          (.setTimeout js/window
                                       (fn [_]
                                         (mapv (fn [child]
                                                 (set! (.-data child) evidence-id))
                                               (array-seq wrapper.children)))
                                       100)))))
                  (range (:start page-range) (inc (:end page-range)))
                  html-ranges))))))

(defonce ^js pdfjs nil)

(defn render-document
  "Receives PDF document data and loads it into pages"
  [{:keys [url
           pdf-doc
           pdf-ref
           loaded
           set-pdf-doc
           on-load
           selected-paragraph
           width
           scale
           id-prefix]}]
  (when-not (or pdf-doc loaded)
    (js-await
     [^js mod (esm/dynamic-import "/js/pdfjs/pdf.min.mjs")]
     (set! pdfjs mod)
     (set! (.. mod -GlobalWorkerOptions -workerSrc) "/js/pdfjs/pdf.worker.min.mjs")
     (-> (.getDocument js/pdfjsLib url)
         (.-promise)
         (.then (fn [^js pdf]
                  (set-pdf-doc pdf)
                  (mapv
                   (partial render-page
                            pdf
                            pdf-ref
                            selected-paragraph
                            on-load
                            width
                            scale
                            id-prefix)
                   (range 1 (inc (.-numPages pdf))))))))))
